import Calendar from '@/components/calendar/Calendar';
import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import {
    DATE_YMD_2,
    ekDomain,
    EMAIL_VALIDATE_REGEX,
    MAX_LENGTH_NUMBER,
    NUMBER_FULL_HALF_VALIDATE_REGEX,
    PASSWORD_VALIDATE_REGEX,
    ROUTES,
    TEST_EK_ANSWER
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams
} from 'react-router-dom';
import TestCollapsible from './components/TestCollapsible';
import InfoCollapsible from './components/InfoCollapsible';
import { Checkbox } from '@/components/common/Checkbox';
import { isPathEk } from '@/utils/routes';
import StepProgressBar from '@/components/general/step-indicator';
import ExampleHelp from './components/ExampleHelp';
import { Textarea } from '@/components/common/Textarea';
import TestForm from './components/TestForm';
import AnimateButton from '@/components/general/button-custom/AnimateButton';

export default observer(function RegisterStep2EkPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid') ?? '';
    const emailSearchParams = searchParams.get('email') ?? '';
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        authStore: { tokenEk, registerEK, checkUuid, isCompleteTestEK, setObservable },
        generalStore: { licenseTypeEk, getLicenseEk },
        modalStore: { showAlertModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        licenseType: yup
            .string()
            .required('form_error_validate.required'),
        licenseBeforeImageId: yup
            .number()
            .required('form_error_validate.required'),
        licenseAfterImageId: yup
            .number()
            .required('form_error_validate.required'),
        fullname: yup
            .string()
            .required('form_error_validate.required'),
        birthOfDate: yup
            .date()
            .required('form_error_validate.required'),
        zipCode: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        address: yup
            .string(),
        eRegister: yup
            .string()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email'),
        phone: yup
            .string()
            .required('form_error_validate.required')
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        furiganaName: yup
            .string(),
        passwordRegister: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        confirmPassword: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password')
            .oneOf(
                [yup.ref('passwordRegister')],
                'form_error_validate.password_not_match'
            ),
        note: yup.string(),
        agree: yup.boolean(),
        agreeConfirmed: yup.boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // form
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            licenseType: '',
            licenseBeforeImageId: undefined,
            licenseAfterImageId: undefined,
            fullname: '',
            birthOfDate: undefined,
            zipCode: '',
            address: '',
            eRegister: '',
            phone: '',
            furiganaName: '',
            passwordRegister: '',
            confirmPassword: '',
            note: '',
            agree: false,
            agreeConfirmed: false
        }
    });

    // state
    const watchAgree = form.watch('agree');
    const watchAgreeConfirmed = form.watch('agreeConfirmed');

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const [testCollapseTitle, setTestCollapseTitle] = useState<string>(
        t('test.title_collapse')
    );

    // lifecycle
    useEffect(() => {
        getLicenses();
    }, [tokenEk]);

    useEffect(() => {
        form.setValue('eRegister', emailSearchParams ?? '');
    }, [emailSearchParams]);

    // function
    const getLicenses = async () => {
        await flowResult(getLicenseEk());
    };

    const onSubmit = async (data: FormData) => {
        const {
            licenseType,
            licenseBeforeImageId,
            licenseAfterImageId,
            fullname,
            birthOfDate,
            zipCode,
            address,
            eRegister,
            phone,
            furiganaName,
            passwordRegister,
            confirmPassword,
            note
        } = data;

        const dataSubmit = {
            licenseType:
                licenseType && !isNaN(Number(licenseType)) ? [Number(licenseType)] : [],
            licenseBeforeImageId,
            licenseAfterImageId,
            fullname,
            birthOfDate: formatDateTime(birthOfDate || '', DATE_YMD_2),
            zipCode,
            address,
            email: eRegister,
            phone,
            furiganaName: furiganaName,
            password: passwordRegister,
            confirmPassword,
            note
        };

        const checkUuidRes = await flowResult(checkUuid({ uuid }));
        if (checkUuidRes) {
            const res = await flowResult(registerEK(dataSubmit));
            if (res && res.ok) {
                navigate(`/${ekDomain}${ROUTES.signup3.href}`, {
                    state: {
                        email: eRegister
                    }
                });
                // showAlertModal({
                //     id: 'success',
                //     type: 'success',
                //     content: t('messages.sign_up_success'),
                //     onClickXIcon: () => {
                //         navigate(`/${ekDomain}${ROUTES.signup3.href}`, {
                //             state: {
                //                 email: eRegister
                //             }
                //         });
                //     }
                // });
            }
        }
    };

    // form test
    const onSubmitTest = async (dataForm: any) => {
        if (dataForm && checkAnswers(TEST_EK_ANSWER, dataForm)) {
            showAlertModal({
                id: 'success',
                type: 'success',
                content: t('test.test_success'),
                onClickXIcon: () => {
                    setObservable('isCompleteTestEK', true);

                    const today = new Date();
                    const completedDay = today.getDate();
                    const completedMonth = today.getMonth();
                    const completedTitle = t('test.title_collapse_success', {
                        day: completedDay,
                        month: completedMonth + 1
                    });
                    setTestCollapseTitle(completedTitle);
                }
            });
        } else {
            showAlertModal({
                id: 'test-error',
                type: 'error',
                content: t('test.test_fail')
            });
        }
    };

    const checkAnswers = (
        correctAnswers: Record<string, boolean>,
        userAnswers: Record<string, boolean>
    ): boolean => {
        for (let i = 1; i <= Object.keys(correctAnswers).length; i++) {
            let key = `QUESTION_${i}`;
            let userKey = `ques${i}`;

            if (correctAnswers[key] !== userAnswers[userKey]) {
                return false;
            }
        }
        return true;
    };

    return (
        <div>
            <h3 className='title-1 text-center mb-3'>
                {t('words_title.register_account')}
            </h3>
            <div className='pt-6'>
                <StepProgressBar
                    startingStep={1}
                    wrapperClass='w-full'
                    steps={[
                        {
                            label: t('words_title.register_page.step_1'),
                            name: 'register_page.step_1'
                        },
                        {
                            label: t('words_title.register_page.step_2'),
                            name: 'register_page.step_2'
                        },
                        {
                            label: t('words_title.register_page.step_3'),
                            name: 'register_page.step_3'
                        }
                    ]}
                />
            </div>
            <div className='h-8 w-full'></div>
            <TestCollapsible
                title={testCollapseTitle}
                className='-mx-6 mb-6'
                classNameTitle={isCompleteTestEK ? 'text-green-1' : 'text-red-2'}
            >
                <TestForm onSubmit={value => onSubmitTest(value)} />
            </TestCollapsible>
            <InfoCollapsible
                title={t('words_title.register_account')}
                className='-mx-6'
                isExpand={false}
                disabled={!isCompleteTestEK}
            >
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div>
                            <h2 className='title-2'>{t('words_title.user_infomation')}</h2>
                            <div className='mt-4 gap-4 md:gap-6'>
                                <FormField
                                    control={form.control}
                                    name='licenseType'
                                    render={({ field }) => (
                                        <FormItem className='col-span-2'>
                                            <FormLabel required>
                                                {t('field.type_of_document')}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    value={field.value?.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.code);
                                                        form.setValue('licenseType', item?.code);
                                                    }}
                                                    items={licenseTypeEk.map(item => ({
                                                        label: item.name,
                                                        value: item.id?.toString(),
                                                        code: item.id
                                                    }))}
                                                    search={false}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className='my-4'>
                                <ExampleHelp />
                            </div>

                            <div className='mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6'>
                                <div className=''>
                                    <ImageUploadAccount
                                        form={form}
                                        filedName='licenseBeforeImageId'
                                        caption={t('words_title.before')}
                                        className='h-56'
                                        isOcrNoToken={true}
                                        required={true}
                                    />
                                </div>
                                <div className=''>
                                    <ImageUploadAccount
                                        filedName='licenseAfterImageId'
                                        form={form}
                                        caption={t('words_title.after')}
                                        className='h-56'
                                        required={true}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='mt-4'>
                            <div className='flex flex-col gap-4'>
                                <FormField
                                    control={form.control}
                                    name='fullname'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required>{t('field.name_or_company_name')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='furiganaName'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t('field.furigana_name')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    autoComplete='off'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='birthOfDate'
                                    render={({ field, fieldState }) => (
                                        <FormItem>
                                            <FormLabel required>
                                                {t('field.date_of_birth')}
                                            </FormLabel>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={date => field.onChange(date)}
                                                    useIconRemove={false}
                                                    required={false}
                                                    isInvalid={!!fieldState.error}
                                                    disabled={[
                                                        {
                                                            after: new Date()
                                                        }
                                                    ]}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='zipCode'
                                    render={({ field }) => (
                                        <FormItem className='col-span-full'>
                                            <FormLabel>{t('field.post_code')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    isHalfWidth
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='address'
                                    render={({ field }) => (
                                        <FormItem className='col-span-full'>
                                            <FormLabel>{t('field.address')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='eRegister'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required>{t('field.email')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    disabled={true}
                                                    autoComplete='off'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='phone'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required>
                                                {t('field.phone_number')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='text'
                                                    placeholder={t('placeholder.enter')}
                                                    maxLength={MAX_LENGTH_NUMBER}
                                                    isHalfWidth
                                                    autoComplete='new-phone'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='passwordRegister'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required>{t('field.password')}</FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showPassword ? 'password' : 'text'}
                                                    placeholder={t('placeholder.enter')}
                                                    onToggleIcon={() => setShowPassword(!showPassword)}
                                                    autoComplete='new-password'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='confirmPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required>
                                                {t('field.confirm_password')}
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showConfirmPassword ? 'password' : 'text'}
                                                    placeholder={t('placeholder.enter')}
                                                    onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    autoComplete='off'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='note'
                                    render={({ field }) => (
                                        <FormItem className='col-span-full'>
                                            <FormLabel>{t('field.note')}</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder={t(
                                                        'sentences.register_note_description'
                                                    )}
                                                    className='min-h-28'
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className='mt-4 flex flex-col gap-4'>
                            <FormField
                                control={form.control}
                                name='agree'
                                render={({ field }) => (
                                    <FormItem className='flex items-center'>
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormLabel className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                            <Trans
                                                i18nKey='sentences.agree_register_1'
                                                components={{
                                                    Link: (
                                                        <Link
                                                            to={isEk ? `/${ekDomain}/${ROUTES.privacyPolicy.hrefEK}` : ROUTES.privacyPolicy.href}
                                                            target='_blank'
                                                            className='link-1'
                                                        />
                                                    )
                                                }}
                                            />
                                        </FormLabel>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='agreeConfirmed'
                                render={({ field }) => (
                                    <FormItem className='flex items-center'>
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormLabel className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                            {t('sentences.confirmed_info_register')}
                                        </FormLabel>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div>
                                <span className='before:content-["_*"] before::inline-block before:text-red-700'>
                                    {t('sentences.note_register')}
                                </span>
                            </div>
                        </div>

                        <div className='flex justify-center'>
                            <AnimateButton
                                contentComp={t('button.send')}
                                parentClassName='mt-8 w-[208px] h-[48px]'
                                disabled={watchAgree !== true || watchAgreeConfirmed !== true}
                                type='submit'
                                outline
                            >
                            </AnimateButton>
                        </div>
                    </form>
                </Form>
            </InfoCollapsible>
        </div>
    );
});
